import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Search,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  startLoader,
  stopLoader,
} from "../../_store/reducers/loaderReducer.js";
import { getScenarios, deleteScenario, getCategories } from "../../requests.js";
import { FormControl, InputGroup } from "react-bootstrap";
import styles from "../../_css/RoleplayManagement.module.css";
import { notifySuccess } from "../helper/Toaster.js";

const RoleManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const debounceTimeoutRef = useRef(null);

  // Core State - Initializing from location.state if returning from edit
  const [page, setPage] = useState(location?.state?.page || 1);
  const [categories, setCategories] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // UI State
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState(null);

  // Filter State - Initializing from location.state if returning from edit
  const [selectedType, setSelectedType] = useState(location?.state?.selectedType || "Standard");
  const [selectedCategoryId, setSelectedCategoryId] = useState(location?.state?.categoryId || null);
  const [searchTerm, setSearchTerm] = useState(location?.state?.searchTerm || "");
  const [searchDebounce, setSearchDebounce] = useState(location?.state?.searchTerm || "");

  // Helper to format Label(lang)
  const formatCategoryDisplay = (label, language) => {
    if (!label) return "Category";
    const langCode = language?.toLowerCase() === "english" ? "en" :
      language?.toLowerCase() === "dutch" ? "nl" :
        language?.toLowerCase() === "french" ? "fr" :
          language?.substring(0, 2).toLowerCase();
    return langCode ? `${label}(${langCode})` : label;
  };

  // 1. Fetch Categories once on mount
  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const data = await getCategories();
        setCategories(data?.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategoriesData();
  }, []);

  // 2. Fetch Scenarios when filters change
  useEffect(() => {
    const fetchScenariosData = async () => {
      try {
        dispatch(startLoader());

        const params = {
          type: selectedType,
          category: selectedCategoryId,
          search: searchDebounce,
        };

        const response = await getScenarios(params);
        setCategoryData(response?.data || []);
        setFilteredData(response?.data || []);
      } catch (error) {
        console.error("Error fetching scenarios:", error);
      } finally {
        dispatch(stopLoader());
      }
    };

    if (categories.length > 0) {
      fetchScenariosData();
    }
  }, [dispatch, selectedType, selectedCategoryId, searchDebounce, categories]);

  // Handle Outside Click for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowCategoryDropdown(false);
        setMenuOpenIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDeleteScenario = async (id) => {
    try {
      dispatch(startLoader());
      const response = await deleteScenario(id);
      if (response) {
        const newData = categoryData.filter((scenario) => scenario.id !== id);
        setCategoryData(newData);
        setFilteredData(newData);
        setShowDeleteModal(false);
        setScenarioToDelete(null);
        notifySuccess("Scenario deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting scenario:", error);
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchDebounce(value);
      setPage(1);
    }, 700);
  };

  const handleCategorySelection = (id) => {
    setSelectedCategoryId(prev => prev === id ? null : id);
    setPage(1);
    setShowCategoryDropdown(false);
  };

  const clearAllFilters = () => {
    setSelectedType("Standard");
    setSelectedCategoryId(null);
    setSearchTerm("");
    setSearchDebounce("");
    setPage(1);
  };

  // Find active category for display
  const activeCategoryObj = categories.find(c => c.value === selectedCategoryId);

  // Pagination logic
  const rowsPerPage = 8;
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleEditNavigate = (row) => {
    navigate("/roleplayManagement/editscenario", {
      state: {
        scenarioData: row,
        categoryId: selectedCategoryId,
        page: page,
        searchTerm: searchDebounce,
        selectedType: selectedType
      }
    });
  };

  return (
    <div>
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Delete Roleplay</h3>
            <p className={styles.modalText}>
              Are you sure you want to delete this roleplay "{scenarioToDelete?.title}"? This action cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button onClick={() => setShowDeleteModal(false)} className={`${styles.modalButton} ${styles.modalButtonBack}`}>Back</button>
              <button onClick={() => handleDeleteScenario(scenarioToDelete.id)} className={`${styles.modalButton} ${styles.modalButtonDelete}`}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerFilters}>
            <InputGroup className="w-auto positon-relative align-items-center rp-search-input">
              <Search className="search-icon" size={16} />
              <FormControl
                placeholder="Search role-plays..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ marginLeft: "12px" }}
                className="rp-search-input border-0"
              />
            </InputGroup>

            <div className="dropdown-container" style={{ position: "relative" }}>
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className={styles.categoryButton}
                style={{ color: selectedCategoryId ? "#111827" : "#6b7280" }}
              >
                {selectedCategoryId
                  ? formatCategoryDisplay(activeCategoryObj?.label, activeCategoryObj?.language)
                  : "Category"}
                {showCategoryDropdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showCategoryDropdown && (
                <div className={`${styles.categoryDropdown} ${styles.categoryDropdownWithMargin}`}>
                  {categories?.map((item, idx) => (
                    <div
                      key={item.value || idx}
                      className={`${styles.categoryDropdownItem} ${selectedCategoryId === item.value ? styles.categoryDropdownItemActive : ""}`}
                      onClick={() => handleCategorySelection(item.value)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span>{formatCategoryDisplay(item.label, item.language)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(selectedType !== "Standard" || selectedCategoryId || searchDebounce) && (
              <button onClick={clearAllFilters} className={styles.categoryButton}>Clear Filters</button>
            )}
          </div>
          <button onClick={() => navigate("/roleplayManagement/addScenario")} className={styles.addCategoryButton}>
            Add Scenario
          </button>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={`${styles.tableHeader} ${styles.tableHeaderFirst}`} style={{ minWidth: "60px" }}>
                  <div className={styles.tableHeaderContent}>
                    <input type="checkbox" className={styles.checkbox} />
                    <span className={styles.tableHeaderText}>S.L</span>
                  </div>
                </th>
                <th className={styles.tableHeader} style={{ minWidth: "80px" }}>Category</th>
                <th className={styles.tableHeader} style={{ minWidth: "200px" }}>Sub-Category</th>
                <th className={styles.tableHeader} style={{ minWidth: "280px" }}>Title of Scenario</th>
                <th className={styles.tableHeader} style={{ minWidth: "100px" }}>Type</th>
                <th className={styles.tableHeader} style={{ minWidth: "350px" }}>Linked Competency</th>
                <th className={`${styles.tableHeader} ${styles.tableHeaderLast}`} style={{ minWidth: "80px" }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData?.length > 0 ? (
                paginatedData?.map((row, index) => {
                  const isLastRow = index === paginatedData.length - 1;
                  return (
                    <tr key={row.id} className={styles.tableRow}>
                      <td className={`${styles.tableCell} ${styles.tableCellFirst}`} style={{ borderBottomLeftRadius: isLastRow ? "12px" : "0" }}>
                        <div className={styles.tableCellContent}>
                          <input type="checkbox" className={styles.checkbox} />
                          <span className={styles.tableCellText}>{startIndex + index + 1}</span>
                        </div>
                      </td>
                      <td className={styles.tableCell}><span className={styles.tableCellText}>{row.category}</span></td>
                      <td className={styles.tableCell}><span className={styles.tableCellText}>{row.title}</span></td>
                      <td className={styles.tableCell}><span className={styles.tableCellText}>{row.scenerio_title}</span></td>
                      <td className={styles.tableCell}><span className={styles.tableCellText}>{row.type}</span></td>
                      <td className={styles.tableCell}>
                        <div className={styles.competencyWrapper}>
                          {row["Linked Competency"]?.map((competency, cIdx) => (
                            <span key={cIdx} className={styles.competencyBadge}>{competency.split(" – ")[0].trim()}</span>
                          ))}
                        </div>
                      </td>
                      <td className={`dropdown-container ${styles.tableCell} ${styles.tableCellLast}`} style={{ borderBottomRightRadius: isLastRow ? "12px" : "0" }}>
                        <div className={styles.tableCellRelative}>
                          <button onClick={() => setMenuOpenIndex(menuOpenIndex === index ? null : index)} className={styles.menuButton}>
                            <MoreVertical size={18} color="#6B7280" />
                          </button>
                          {menuOpenIndex === index && (
                            <div className={styles.menuDropdown}>
                              <div className={styles.menuDropdownItem} onClick={() => handleEditNavigate(row)}>Edit Scenario</div>
                              <div className={styles.menuDropdownItem} onClick={() => { setScenarioToDelete(row); setShowDeleteModal(true); setMenuOpenIndex(null); }}>Delete Scenario</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="7" className={styles.emptyState}>No scenarios found matching your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.paginationContainer}>
          {totalPages > 1 && (
            <div className={styles.paginationWrapper}>
              <button disabled={page === 1} onClick={() => handlePageChange(page - 1)} className={styles.paginationButton}>
                <ChevronLeft size={16} /> Previous
              </button>
              <div className={styles.paginationNumbers}>
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePageChange(idx + 1)}
                    className={`${styles.paginationNumber} ${page === idx + 1 ? styles.paginationNumberActive : ""}`}
                  >
                    {(idx + 1).toString().padStart(2, "0")}
                  </button>
                ))}
              </div>
              <button disabled={page === totalPages} onClick={() => handlePageChange(page + 1)} className={`${styles.paginationButton} ${styles.paginationButtonNext}`}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManager;